/**
 * Users - Módulo para la gestión de usuarios
 */
import $ from 'jquery';
import 'datatables.net-bs5';
import 'datatables.net-responsive-bs5';
import { escapeHtmlAttribute, getSpanishLanguage } from '@/helpers';
export class Users {
    constructor() {
        this.TABLE_ID = '#usersTable';
        this.dataTable = null;
        this.deactivateModal = null;
        this.activateModal = null;
        this.baseUrl = window.location.origin;
        this.init();
    }
    init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.initializeModule();
            });
        }
        else {
            this.initializeModule();
        }
    }
    initializeModule() {
        this.initializeTable();
        this.initializeModals();
        this.setupEventListeners();
    }
    setupEventListeners() {
        // Escuchar cambios de tema para ajustar la tabla si es necesario
        window.addEventListener('theme-changed', () => {
            this.adjustTableForTheme();
        });
        // Manejar clicks en botones de acción (delegación de eventos)
        document.addEventListener('click', (e) => {
            const target = e.target;
            const deactivateBtn = target.closest('[data-action="deactivate"]');
            const activateBtn = target.closest('[data-action="activate"]');
            if (deactivateBtn) {
                e.preventDefault();
                this.handleDeactivate(deactivateBtn);
            }
            if (activateBtn) {
                e.preventDefault();
                this.handleActivate(activateBtn);
            }
        });
    }
    adjustTableForTheme() {
        // Ajustes visuales cuando cambia el tema (si es necesario)
        if (this.dataTable) {
            this.dataTable.draw(false);
        }
    }
    initializeModals() {
        const deactivateModalEl = document.getElementById('deactivateModal');
        const activateModalEl = document.getElementById('activateModal');
        if (deactivateModalEl) {
            this.deactivateModal = new window.bootstrap.Modal(deactivateModalEl);
        }
        if (activateModalEl) {
            this.activateModal = new window.bootstrap.Modal(activateModalEl);
        }
    }
    handleDeactivate(button) {
        const id = button.getAttribute('data-id');
        const name = button.getAttribute('data-name');
        const email = button.getAttribute('data-email');
        if (!id || !name) {
            console.error('Missing id or name attribute');
            return;
        }
        const userNameEl = document.getElementById('userName');
        const userEmailEl = document.getElementById('userEmail');
        const confirmBtn = document.getElementById('confirmDeactivateBtn');
        if (userNameEl) {
            userNameEl.textContent = name;
        }
        if (userEmailEl && email) {
            userEmailEl.textContent = email;
        }
        if (confirmBtn) {
            confirmBtn.setAttribute('href', `${this.baseUrl}/users/change-status/${id}`);
        }
        if (this.deactivateModal) {
            this.deactivateModal.show();
        }
    }
    handleActivate(button) {
        const id = button.getAttribute('data-id');
        const name = button.getAttribute('data-name');
        const email = button.getAttribute('data-email');
        if (!id || !name) {
            console.error('Missing id or name attribute');
            return;
        }
        const userNameEl = document.getElementById('activateUserName');
        const userEmailEl = document.getElementById('activateUserEmail');
        const confirmBtn = document.getElementById('confirmActivateBtn');
        if (userNameEl) {
            userNameEl.textContent = name;
        }
        if (userEmailEl && email) {
            userEmailEl.textContent = email;
        }
        if (confirmBtn) {
            confirmBtn.setAttribute('href', `${this.baseUrl}/users/change-status/${id}`);
        }
        if (this.activateModal) {
            this.activateModal.show();
        }
    }
    initializeTable() {
        const tableElement = document.querySelector(this.TABLE_ID);
        if (!tableElement) {
            console.warn('Users table element not found');
            return;
        }
        try {
            this.dataTable = $(this.TABLE_ID).DataTable(this.getTableConfig());
        }
        catch (error) {
            console.error('Error initializing DataTables:', error);
        }
    }
    getTableConfig() {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        return {
            processing: true,
            serverSide: true,
            responsive: false,
            language: getSpanishLanguage(),
            ajax: {
                url: `${this.baseUrl}/users/get-list`,
                type: 'POST',
                data: {
                    'csrf_token': csrfToken || ''
                },
                error: (xhr, error, thrown) => {
                    console.error('Error al cargar los datos:', error);
                    const tbody = document.querySelector(`${this.TABLE_ID} tbody`);
                    if (tbody) {
                        tbody.innerHTML = '<tr><td colspan="6" class="text-center text-danger">Error al cargar los datos. Por favor, recargue la página.</td></tr>';
                    }
                }
            },
            columns: [
                {
                    data: 'name_user',
                    name: 'name_user',
                    className: 'text-nowrap',
                },
                {
                    data: 'email_user',
                    name: 'email_user',
                    className: 'text-nowrap',
                },
                {
                    data: 'name_rol',
                    name: 'name_rol',
                    className: 'text-nowrap',
                },
                {
                    data: 'status_user',
                    name: 'status_user',
                    className: 'text-nowrap',
                    render: (data) => this.renderEstatus(data)
                },
                {
                    data: 'id_user',
                    name: 'id_user',
                    className: 'text-center text-nowrap col-acciones',
                    orderable: false,
                    render: (data, type, row) => this.renderAcciones(row)
                }
            ],
            order: [[1, 'asc']],
            pageLength: 25,
            lengthMenu: [[10, 25, 50, 100], [10, 25, 50, 100]]
        };
    }
    renderEstatus(data) {
        const status = parseInt(data);
        switch (status) {
            case 0:
                return '<span class="badge bg-danger"><i class="bi bi-x-circle me-1"></i>Inactivo</span>';
            case 2:
                return '<span class="badge bg-warning"><i class="bi bi-x-circle me-1"></i>Bloquedo, reiniciar contraseña</span>';
            default:
                return '<span class="badge bg-success"><i class="bi bi-check-circle me-1"></i>Activo</span>';
        }
    }
    renderAcciones(row) {
        let { can_edit, can_change_status, can_view_history, can_reset_password, name_user: name, id_user: id, email_user: email, status_user: status } = row;
        status = parseInt(status);
        let buttons = `
            <div class="btn-group" role="group">
                ${can_view_history ? `
                        <a href="${this.baseUrl}/users/history/${id}"
                        class="btn btn-sm btn-outline-info"
                        title="Ver acciones del usuario">
                            <i class="bi bi-clock-history"></i>
                        </a>
                    ` : ''}
                ${can_edit ? `
                        <a href="${this.baseUrl}/users/edit/${id}"
                        class="btn btn-sm btn-outline-primary"
                        title="Editar">
                            <i class="bi bi-pencil"></i>
                        </a>
                    ` : ''}
        `;
        if (can_change_status) {
            if (status === 1) {
                buttons += `
                    <button type="button"
                            class="btn btn-sm btn-outline-danger"
                            data-action="deactivate"
                            data-id="${id}"
                            data-name="${escapeHtmlAttribute(name)}"
                            data-email="${escapeHtmlAttribute(email)}"
                            title="Desactivar">
                        <i class="bi bi-dash-circle"></i>
                    </button>
                `;
            }
            else {
                buttons += `
                    <button type="button"
                            class="btn btn-sm btn-outline-success"
                            data-action="activate"
                            data-id="${id}"
                            data-name="${escapeHtmlAttribute(name)}"
                            data-email="${escapeHtmlAttribute(email)}"
                            title="Activar">
                        <i class="bi bi-check-circle"></i>
                    </button>
                `;
            }
        }
        if (can_reset_password) {
            buttons += `
                <a href="${this.baseUrl}/users/reset-password/${id}"
                    class="btn btn-sm btn-outline-secondary"
                    title="Resetear password">
                    <i class="bi bi-key"></i>
                </a>
            `;
        }
        buttons += '</div>';
        return buttons;
    }
    /**
     * Obtiene la instancia del DataTable
     */
    getTable() {
        return this.dataTable;
    }
    /**
     * Recarga los datos de la tabla
     * @param resetPaging Si es true, resetea la paginación a la primera página
     */
    reload(resetPaging = false) {
        if (this.dataTable) {
            this.dataTable.ajax.reload(null, resetPaging);
        }
    }
    /**
     * Obtiene los datos seleccionados en la tabla
     */
    getSelectedRows() {
        if (this.dataTable) {
            return this.dataTable.rows('.selected').data().toArray();
        }
        return [];
    }
    /**
     * Filtra la tabla por una columna específica
     * @param column Índice de la columna
     * @param value Valor a buscar
     */
    filterByColumn(column, value) {
        if (this.dataTable) {
            this.dataTable.column(column).search(value).draw();
        }
    }
    /**
     * Limpia todos los filtros de la tabla
     */
    clearFilters() {
        if (this.dataTable) {
            this.dataTable.search('').columns().search('').draw();
        }
    }
}
export default Users;
